<template>
    <Modal2
        v-if="visible"
        design="v3"
        class-name="bulk-tags-modal"
        :show-footer="false"
        :show-title="false"
        @cancel="onCancel"
    >
        <div class="bulk-tags-modal-content">
            <div class="modal-section">
                <div class="selection-wrapper">
                    <div class="section-title">
                        <i class="material-icons">local_offer</i>
                        <span>{{ $t("auto.tagSelection") }}</span>
                    </div>

                    <div class="form-field">
                        <label class="field-label">{{ $t("auto.tags") }}</label>
                        <a-select
                            v-model="selectedTags"
                            mode="multiple"
                            size="large"
                            :placeholder="$t('auto.selectTag')"
                            :filter-option="filterOption"
                            show-search
                            class="tags-select"
                        >
                            <a-select-option
                                v-for="tag in availableTags"
                                :key="tag._id"
                                :value="tag._id"
                            >
                                {{ tag.name }}
                            </a-select-option>
                        </a-select>
                        <div class="field-hint">
                            <i class="material-icons">lightbulb_outline</i>
                            <span>{{ $t("auto.canSelectMultipleTags") }}</span>
                        </div>
                    </div>
                </div>

                <div v-if="selectedTags.length > 0" class="preview-section">
                    <div class="preview-header">
                        <div class="header-left">
                            <i class="material-icons">sell</i>
                            <span>{{ $t("auto.selectedTags") }}</span>
                        </div>
                        <div class="count-badge">{{ selectedTags.length }}</div>
                    </div>
                    <div class="tags-wrapper">
                        <div 
                            v-for="tagId in selectedTags" 
                            :key="tagId" 
                            class="tag-chip"
                        >
                            <i class="material-icons">label</i>
                            <span>{{ getTagName(tagId) }}</span>
                            <div class="tag-check">
                                <i class="material-icons">check</i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="modal-footer">
                <SupsisButton @click="onCancel">{{ $t("auto.cancel") }}</SupsisButton>
                <SupsisButton primary @click="onConfirm">
                    <i class="material-icons">label</i>
                    {{ $t("auto.addTag") }}
                </SupsisButton>
            </div>
        </div>
    </Modal2>
</template>

<script>
import Modal2 from "@/components/common/Modal2.vue";
import SupsisButton from "@/views/Pages/Settings/components/SupsisButton.vue";
import { mapState } from "vuex";

export default {
    name: "BulkAddTagsModal",
    components: { Modal2, SupsisButton },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        selectedCount: {
            type: Number,
            default: 0,
        },
    },
    data: function () {
        return {
            selectedTags: [],
        };
    },
    computed: {
        ...mapState({
            tags: (state) => state.tag.tags.data,
        }),
        availableTags: function () {
            return this.tags.filter((tag) => !tag.isDeleted);
        },
    },
    methods: {
        getTagName: function (tagId) {
            const tag = this.tags.find((t) => t._id === tagId);
            return tag ? tag.name : tagId;
        },
        filterOption: function (input, option) {
            const tag = this.tags.find((t) => t._id === option.key);
            return tag && tag.name.toLowerCase().includes(input.toLowerCase());
        },
        onConfirm: function () {
            if (this.selectedTags.length === 0) {
                this.$message.warning("Lütfen en az bir etiket seçiniz");
                return;
            }

            this.$emit("confirm", {
                action: "add-tags",
                payload: {
                    tagIds: this.selectedTags,
                },
            });
            this.reset();
        },
        onCancel: function () {
            this.$emit("cancel");
            this.reset();
        },
        reset: function () {
            this.selectedTags = [];
        },
    },
};
</script>

<style lang="scss">
.bulk-tags-modal {
    .custom-modal-body {
        padding: 0 !important;
    }

    .bulk-tags-modal-content {
        display: flex;
        flex-direction: column;
        min-height: 450px;

        .modal-section {
            flex: 1;
            padding: 28px;
            overflow-y: auto;
            max-height: calc(80vh - 200px);
        }

        .selection-wrapper {
            margin-bottom: 28px;

            .section-title {
                display: flex;
                align-items: center;
                gap: 10px;
                margin-bottom: 20px;
                padding-bottom: 12px;
                border-bottom: 2px solid #e8eaed;

                .material-icons {
                    font-size: 24px;
                    color: #22272d;
                }

                span {
                    font-size: 16px;
                    font-weight: 700;
                    color: #22272d;
                    letter-spacing: 0.3px;
                }
            }

            .form-field {
                .field-label {
                    display: block;
                    font-size: 14px;
                    font-weight: 600;
                    color: #495057;
                    margin-bottom: 10px;
                    letter-spacing: 0.3px;
                }

                .tags-select {
                    width: 100%;
                }

                .field-hint {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    margin-top: 10px;
                    font-size: 13px;
                    color: #6c757d;

                    .material-icons {
                        font-size: 16px;
                    }
                }
            }
        }

        .preview-section {
            padding-top: 24px;
            border-top: 2px solid #e8eaed;

            .preview-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 16px;

                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 10px;

                    .material-icons {
                        font-size: 22px;
                        color: #22272d;
                    }

                    span {
                        font-size: 15px;
                        font-weight: 700;
                        color: #22272d;
                        letter-spacing: 0.3px;
                    }
                }

                .count-badge {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 32px;
                    height: 32px;
                    padding: 0 12px;
                    background: linear-gradient(135deg, #22272d 0%, #3d4451 100%);
                    color: white;
                    border-radius: 16px;
                    font-size: 14px;
                    font-weight: 700;
                    box-shadow: 0 2px 8px rgba(34, 39, 45, 0.2);
                }
            }

            .tags-wrapper {
                display: flex;
                flex-wrap: wrap;
                gap: 10px;
            }

            .tag-chip {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 10px 16px;
                background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
                border: 1.5px solid #e8eaed;
                border-radius: 24px;
                transition: all 0.3s ease;
                cursor: default;

                &:hover {
                    border-color: #22272d;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(34, 39, 45, 0.08);
                }

                .material-icons:first-child {
                    font-size: 18px;
                    color: #6c757d;
                }

                span {
                    font-size: 14px;
                    font-weight: 600;
                    color: #22272d;
                }

                .tag-check {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 20px;
                    height: 20px;
                    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
                    border-radius: 50%;

                    .material-icons {
                        font-size: 14px;
                        color: white;
                    }
                }
            }
        }

        .modal-footer {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            gap: 12px;
            padding: 20px 28px;
            border-top: 2px solid #e8eaed;

            .supsis-button {
                height: 44px;
                padding: 0 24px;
                font-size: 15px;
                border-radius: 10px;
                
                .material-icons {
                    font-size: 18px;
                }
            }
        }
    }
}
</style>
